import { useCallback, useEffect, useMemo } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import { generateId, getClipboardFiles } from "@productlane/lib/src/browser"

import { captureError } from "../lib/capture-error"
import { useAttachmentsValidation } from "./useAttachmentsValidation"

export interface FormattedAttachment {
	Content: string
	Name: string
	ContentType: string
	ContentLength?: string | number | null | undefined
	ContentID?: string | undefined
}

type AttachmentState = "loading" | "uploaded" | "error"
export interface Attachment {
	id: string
	file: File
	// set async, so marked as optional
	url?: string | null

	name: string
	type: string
	size: number
	state: AttachmentState
	isPersist?: boolean // Optional flag to indicate if the attachment should be persisted
}

interface AttachmentsState {
	attachments: Record<string, Attachment[]> // Keyed by contextId
	setAttachments: (contextId: string, attachments: Attachment[]) => void
	removeAttachment: (contextId: string, attachmentId: string) => void
	clearAttachments: (contextId: string) => void
}

interface UseAttachmentsParams {
	contextId: string
	initialAttachments?: {
		Content: string
		Name: string
		ContentType: string
		ContentLength?: string | number | null | undefined
		ContentID?: string | undefined
	}[] // Initial attachments to set for the contextId
	errorHandler: (options: { title: string; description: string }) => void
	getSignedUrl: (params: {
		fileName: string
		fileType: string
	}) => Promise<{ uploadUrl: string; fileUrl: string }>
	deleteFileUrl: (params: { fileUrl: string }) => Promise<void>
	isPersist?: boolean // Optional flag to indicate if the attachment should be persisted
}

interface UseAttachmentsReturn {
	attachments: Attachment[]
	formattedAttachments: FormattedAttachment[]
	addAttachments: (files: FileList | Array<File>) => Promise<void>
	addPasteAttachments: (
		items: DataTransferItemList | null | undefined,
	) => Promise<void>
	addExistingAttachment: (params: {
		url: string
		name: string
		type: string
	}) => void
	removeAttachment: (fileId: string) => Promise<void>
	updateAttachmentUrl: (data: { fileName: string; url: string }) => void
	clearAttachments: () => void
	isAttachmentsLoading: boolean
}

export const useAttachmentsStore = create<AttachmentsState>()(
	persist(
		(set, _) => ({
			attachments: {},
			setAttachments: (contextId: string, attachments: Attachment[]) =>
				set((state) => ({
					attachments: {
						...state.attachments,
						[contextId]: attachments,
					},
				})),
			removeAttachment: (contextId: string, attachmentId: string) =>
				set((state) => {
					const currentAttachments = state.attachments[contextId] || []
					const updatedAttachments = currentAttachments.filter(
						(attachment) => attachment.id !== attachmentId,
					)
					return {
						attachments: {
							...state.attachments,
							[contextId]: updatedAttachments,
						},
					}
				}),
			clearAttachments: (contextId: string) =>
				set((state) => {
					const { [contextId]: _, ...rest } = state.attachments
					return { attachments: rest }
				}),
		}),
		{
			name: "attachments-store",
			partialize: (state) => {
				// Exclude loading attachments from being persisted
				const filteredAttachments: Record<string, Attachment[]> = {}
				Object.entries(state.attachments).forEach(
					([contextId, attachments]) => {
						filteredAttachments[contextId] = attachments.filter(
							(attachment) =>
								attachment.state !== "loading" && attachment.isPersist,
						)
					},
				)
				return { attachments: filteredAttachments }
			},
		},
	),
)

const UPLOAD_RETRY_DELAYS_MS = [500, 1500]

// A request the browser could not send at all (offline, dropped connection).
function isNetworkFailure(error: unknown): boolean {
	return (
		error instanceof TypeError &&
		/fetch|network|load failed/i.test(error.message)
	)
}

// The storage host often drops one connection and answers the next try.
async function putWithRetry(url: string, init: RequestInit): Promise<Response> {
	for (let attempt = 0; ; attempt++) {
		try {
			return await fetch(url, init)
		} catch (error) {
			const delay = UPLOAD_RETRY_DELAYS_MS[attempt]
			if (delay === undefined || !isNetworkFailure(error)) throw error
			await new Promise((resolve) => setTimeout(resolve, delay))
		}
	}
}

export function useAttachments({
	contextId,
	initialAttachments = [], // Initial attachments to set for the contextId
	errorHandler,
	getSignedUrl,
	deleteFileUrl,
	isPersist = true, // Default to true if not provided
}: UseAttachmentsParams): UseAttachmentsReturn {
	const rawAttachments = useAttachmentsStore(
		(state) => state.attachments[contextId],
	)
	const attachments = useMemo(() => rawAttachments || [], [rawAttachments])

	const formattedAttachments = useMemo(() => {
		return attachments.map((attachment) => ({
			Content: attachment.url ?? "",
			ContentLength: attachment.size,
			Name: attachment.name,
			ContentType: attachment.type,
			ContentID: attachment.id,
		}))
	}, [attachments])

	const getInitialAttachments = (initialAttachments: FormattedAttachment[]) => {
		return initialAttachments.map((attachment) => ({
			id: generateId(),
			file: new File([], attachment.Name, {
				type: attachment.ContentType,
			}),
			name: attachment.Name,
			type: attachment.ContentType,
			size: Number(attachment.ContentLength) || 0,
			url: attachment.Content,
			state: "uploaded" as AttachmentState,
			isPersist: isPersist,
		}))
	}
	useEffect(() => {
		// Initialize attachments for the contextId if not already set
		if (!rawAttachments) {
			useAttachmentsStore
				.getState()
				.setAttachments(contextId, getInitialAttachments(initialAttachments))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const getCurrentAttachments = useCallback(
		() => useAttachmentsStore.getState().attachments[contextId] || [],
		[contextId],
	)

	const setAttachments = useAttachmentsStore((state) => state.setAttachments)

	const { validateAttachments } = useAttachmentsValidation({
		exisitingAttachments: attachments,
		errorHandler,
	})

	const convertFilesToAttachments = useCallback(
		(files: FileList | Array<File>): Attachment[] => {
			return Array.from(files).map((file) => ({
				id: generateId(),
				file,
				name: file.name,
				type: file.type,
				size: file.size,
				url: null,
				state: "loading" as AttachmentState,
				isPersist: (isPersist as boolean) ?? true, // Use the isPersist flag if provided
			}))
		},
		[isPersist],
	)

	const removeAttachment = useCallback(
		async (fileId: string) => {
			const attachment = attachments.find((a) => a.id === fileId)

			if (!attachment) {
				return
			}

			setAttachments(
				contextId,
				attachments.filter((a) => a.id !== fileId),
			)

			if (attachment.url) {
				await deleteFileUrl({
					fileUrl: attachment.url,
				})
			}
		},
		[attachments, contextId, deleteFileUrl, setAttachments],
	)

	const uploadAttachment = useCallback(
		async (attachment: Attachment) => {
			const { uploadUrl, fileUrl } = await getSignedUrl({
				fileName: attachment.name,
				fileType: attachment.type,
			})

			if (!uploadUrl) {
				throw new Error("Failed to get upload URL")
			}

			const headers = { "Content-Type": attachment.type }
			await putWithRetry(uploadUrl, {
				method: "PUT",
				body: attachment.file,
				headers,
			})

			return { fileUrl }
		},
		[getSignedUrl],
	)

	const onAttachmentUpload = useCallback(
		async (attachment: Attachment, fileUrl: string) => {
			const currentAttachments = getCurrentAttachments()
			const existingAttachment = currentAttachments.find(
				(a) => a.id === attachment.id,
			)
			if (!existingAttachment) {
				await deleteFileUrl({
					fileUrl,
				})
				return
			}

			const updatedAttachments = currentAttachments.map((a) =>
				a.id === attachment.id
					? { ...a, url: fileUrl, state: "uploaded" as AttachmentState }
					: a,
			)

			setAttachments(contextId, updatedAttachments)
		},
		[contextId, deleteFileUrl, getCurrentAttachments, setAttachments],
	)

	const handleUploadError = useCallback(
		async (error: Error, attachment: Attachment) => {
			const isUnsupportedType = String(error).includes("Unsupported file type")
			if (!isNetworkFailure(error) && !isUnsupportedType) {
				captureError(error, {
					domain: "general",
					action: "upload attachment",
					extra: { contextId, attachmentName: attachment.name },
				})
			}

			await removeAttachment(attachment.id)

			if (isUnsupportedType) {
				errorHandler({
					title: `Unsupported file type`,
					description: `${attachment.name} is not supported. Please upload a valid file.`,
				})
				return
			}

			errorHandler({
				title: `Upload failed`,
				description: `Failed to upload ${attachment.name}. Please try again.`,
			})
		},
		[contextId, removeAttachment, errorHandler],
	)

	const addAttachments = useCallback(
		async (files: FileList | Array<File>) => {
			const attachments = getCurrentAttachments()
			const newAttachments = convertFilesToAttachments(files)
			const validatedNewAttachments = validateAttachments(newAttachments)

			setAttachments(contextId, [...attachments, ...validatedNewAttachments])

			await Promise.all(
				validatedNewAttachments.map(async (attachment) => {
					await uploadAttachment(attachment)
						.then(async ({ fileUrl }) => {
							await onAttachmentUpload(attachment, fileUrl)
						})
						.catch(async (error) => {
							await handleUploadError(error as Error, attachment)
						})
				}),
			)
		},
		[
			getCurrentAttachments,
			convertFilesToAttachments,
			validateAttachments,
			setAttachments,
			contextId,
			uploadAttachment,
			onAttachmentUpload,
			handleUploadError,
		],
	)

	const addPasteAttachments = useCallback(
		async (items: DataTransferItemList | null | undefined) => {
			const files = getClipboardFiles(items)
			if (files.length === 0) {
				return
			}
			await addAttachments(files)
		},
		[addAttachments],
	)

	const updateAttachmentUrl = useCallback(
		(data: { fileName: string; url: string }) => {
			const updatedAttachments = attachments.map((attachment) => {
				if (attachment.name === data.fileName) {
					return { ...attachment, url: data.url }
				}
				return attachment
			})

			setAttachments(contextId, updatedAttachments)
		},
		[attachments, contextId, setAttachments],
	)

	const addExistingAttachment = useCallback(
		(params: { url: string; name: string; type: string }) => {
			const currentAttachments = getCurrentAttachments()
			const newAttachment: Attachment = {
				id: generateId(),
				file: new File([], params.name, { type: params.type }),
				name: params.name,
				type: params.type,
				size: 0,
				url: params.url,
				state: "uploaded",
				isPersist: isPersist ?? true,
			}
			setAttachments(contextId, [...currentAttachments, newAttachment])
		},
		[contextId, getCurrentAttachments, isPersist, setAttachments],
	)

	const clearAttachments = useCallback(() => {
		useAttachmentsStore.getState().clearAttachments(contextId)
	}, [contextId])

	const isAttachmentsLoading = useMemo(() => {
		return attachments.some((attachment) => attachment.state === "loading")
	}, [attachments])

	return {
		attachments,
		formattedAttachments,
		addAttachments,
		addPasteAttachments,
		addExistingAttachment,
		removeAttachment,
		updateAttachmentUrl,
		clearAttachments,
		isAttachmentsLoading,
	}
}
